import G6, { TreeGraph } from '@antv/g6/dist/g6.min';
import { activityTypeOptions, fullOptionsMap, fullOptionsValueMap } from '../options';
import { generateActionBar } from './common';
import {
  conditionContentPaddingY,
  conditionRowHeight,
  conditionRowInnerHeight,
  conditionTitleHeight,
  CustomNodeType,
  splitLineHeight,
  splitLineMargin,
  textareaHeight
} from './config';
import Decimal from 'decimal.js';

export const generateActivityRuleStr = (activityJsonData) => {
  const {
    optionType,
    hasPrecondition,
    conditions,
    conditionsCombine,
    effectConditions,
    effectConditionsCombine,
    equityInformationRule
  } = activityJsonData;

  const linkMap = {
    or: '或',
    and: '且'
  };

  // 构建活动规则文案，整体文案为：[[(前置条件1) 且 (前置条件2)] 且 [(前置条件3) 或 (前置条件4)]] 且 [[(门槛条件1) 且 (门槛条件2)] 且 [(门槛条件3) 或 (门槛条件4)]]，活动权益
  // 其中前置条件、门槛条件不唯一时以()容纳单一条件，如果是多层嵌套，则以[] 容纳，以 且/或 连接多条件，且/或 前后追加 空格，()[] 为半角。前置条件文案及门槛条件文案之间以 且 拼接，门槛条件文案后拼接 ，，再拼接权益信息文案
  let activityRuleStr = '';

  // 根据 conditionsCombine 找到对应条件id拼接前置条件，or转为或，and转为且
  if (hasPrecondition) {
    let preConditionsStr = '';
    conditionsCombine.groups.forEach((group, groupIndex) => {
      let groupStr = '';
      group._uniqueIds.forEach((id, index) => {
        const condition = conditions.find((c) => c._uniqueId === id);

        // 前置条件如：前置条件文案：X 件/中包/PCS{取单位的显示名称} 起订，必定{串联文案} X 款/件/元；
        const unitValue = condition[fullOptionsValueMap[condition.optionType]];
        const unitOption = fullOptionsMap[condition.optionType];
        groupStr += `(${condition.optionValue} ${condition.orderUnit} 起订，必定 ${condition.fullNumber} ${
          unitOption.find((i) => i.value === unitValue)?.label
        }) ${linkMap[group.type]} `;

        if (index === group._uniqueIds.length - 1) {
          // 删除最后一个连接符
          if (group._uniqueIds.length === 1) {
            groupStr = groupStr.substring(0, groupStr.length - 3);
          } else {
            groupStr = `[${groupStr.substring(0, groupStr.length - 3)}]`;
          }
        }
      });

      preConditionsStr += `${groupStr} ${linkMap[conditionsCombine.type]} `;

      if (groupIndex === conditionsCombine.groups.length - 1) {
        // 删除最后一个连接符
        preConditionsStr = preConditionsStr.substring(0, preConditionsStr.length - 3);
      }
    });

    activityRuleStr += `[${preConditionsStr}] 且 `;
  }
  // 门槛规则
  let conditionStr = '';
  effectConditionsCombine.groups.forEach((group, groupIndex) => {
    let groupStr = '';
    group._uniqueIds.forEach((id, index) => {
      const condition = effectConditions.find((c) => c._uniqueId === id);

      // 门槛条件如：X 件/中包/PCS{取单位的显示名称} 起订，订满{串联文案} X 款/件/元；
      const unitValue = condition[fullOptionsValueMap[condition.optionType]];
      const unitOption = fullOptionsMap[condition.optionType];
      groupStr += `(${condition.optionValue} ${condition.orderUnit} 起订，订满 ${condition.fullNumber} ${
        unitOption.find((i) => i.value === unitValue)?.label
      }) ${linkMap[group.type]} `;

      if (index === group._uniqueIds.length - 1) {
        // 删除最后一个连接符
        if (group._uniqueIds.length === 1) {
          groupStr = groupStr.substring(0, groupStr.length - 3);
        } else {
          groupStr = `[${groupStr.substring(0, groupStr.length - 3)}]`;
        }
      }
    });

    conditionStr += `${groupStr} ${linkMap[effectConditionsCombine.type]} `;

    if (groupIndex === effectConditionsCombine.groups.length - 1) {
      // 删除最后一个连接符
      conditionStr = conditionStr.substring(0, conditionStr.length - 3);
    }
  });

  activityRuleStr += `${conditionStr ? `[${conditionStr}]，` : ''}`;

  // 权益信息文案：
  const { reductionOption, giftOption, reductionDiscount, giftDiscount, discountValue, giftItemStr } =
    equityInformationRule;
  let equityInfoStr = '';

  const giftOptionMap = {
    TARGET: '指定赠品 *X',
    CHOOSE_SELF: '赠品自选*X',
    ITEM_FULL_A_TO_A: '料号满 A 赠 A *X',
    ITEM_FULL_A_TO_B: '料号 A 赠 B *X',
    ARTICLE_FULL_A_TO_A: '货号满 A 赠 A *X',
    ARTICLE_FULL_A_TO_B: '货号 A 赠 B *X'
  };

  // 满减：让利 X 元/%
  if (optionType === 'FULL_SUB') {
    if (reductionOption === 'REDUCTION_AMOUNT') {
      equityInfoStr += `让利 ${discountValue} 元`;
      // 循环
      if (reductionDiscount === 'CYCLIC_FULL_DECREMENT') {
        equityInfoStr = `循环${equityInfoStr}`;
      }
    } else if (reductionOption === 'DISCOUNT_RATE') {
      equityInfoStr += `让利 ${(new Decimal("1").minus(new Decimal(discountValue.toString())).mul(new Decimal('100')))}%`;
    }
  }
  // 满赠：
  else if (optionType === 'FULL_GWP') {
    // 赠送一次：指定赠品 *X / 赠品自选*X / 料号满 A 赠 A *X / 货号满 A 赠 A *X / 料号 A 赠 B *X / 货号 A 增 B *X
    equityInfoStr += giftOptionMap[giftOption].replace('X', discountValue);
    // 每满赠：每满赠指定赠品 *X / 赠品自选*X / 料号满 A 赠 A *X / 货号满 A 赠 A *X / 料号 A 赠 B *X / 货号 A 增 B *X
    if (giftDiscount === 'FULL_QUANTITY') {
      equityInfoStr = `每满赠-${equityInfoStr}`;
    }
  }
  // 直降：享受直降价
  else if (optionType === 'DIRECT_DESCENT') {
    equityInfoStr += '享受直降价';
  }else if (optionType === 'SECKILL') { 
    equityInfoStr += '秒杀';
  }

  activityRuleStr += equityInfoStr;

  return activityRuleStr;
};

export const registerActivityTypeShape = (treeGraph: TreeGraph) => {
  G6.registerNode(CustomNodeType.ACTIVITY_TYPE, {
    draw: (cfg, group) => {
      const setVal = ((cfg.jsonData as any).promotionSuperpositionPriority || '').split('#').filter((i) => i != '');

      const diejiaSelects = `
      ${setVal.length ? '<span>可叠加:</span>' : ''}
      ${setVal.reduce((ta, a: any, index) => {
        return (
          ta +
          `
          ${index > 0 ? '<span>-</span>' : ''}
          <input disabled class="condition-number-input" value="${a}" style="width: 54px;height: ${conditionRowInnerHeight}px;"/>
          `
        );
      }, '')}
      `;

      const conditionHtml = `
      <div style="display: flex;align-items: center;column-gap: 4px;height: ${conditionRowHeight}px">
        <select disabled data-id="${
          cfg!.id
        }" class="activity-type-select condition-select" style="height: ${conditionRowInnerHeight}px;" placeholder="">
        ${activityTypeOptions.reduce(
          (t, c) =>
            t +
            `<option value="${c.value}" ${(cfg!.jsonData as any).optionType == c.value ? 'selected' : ''}>${
              c.label
            }</option>`,
          ''
        )}
        </select>
        <span>优先级:</span>
        <input disabled id="input_${cfg!.id}" data-id="${
        cfg!.id
      }" class="activity-type-priority-input condition-number-input" type="number" value="${
        (cfg!.jsonData as any).priority
      }" placeholder="优先级" style="width: 54px;height: ${conditionRowInnerHeight}px;"/>
        ${diejiaSelects}
      </div>
    `;

      return group!.addShape('dom', {
        attrs: {
          width: cfg!.size![0],
          height: cfg!.size![1],
          // 传入 DOM 的 html
          html: `
          <div>
            <div style="background-color: #f2f2f2;border-radius:4px;height: 100%;">
              <div style="background-color: ${
                cfg!.style!.titleBgColor
              }; color: #fff; font-size: 12px;text-align: center;border-radius:4px;height: ${conditionTitleHeight}px;line-height: ${conditionTitleHeight}px;">
              ${cfg!.title}
              </div>
              <div style="padding: ${conditionContentPaddingY}px ${conditionContentPaddingY * 2}px;">
                <div style="display: flex;">
                  ${conditionHtml}
                </div>
                <div style="height: ${splitLineHeight}px;margin: ${splitLineMargin}px 0;background-color: ${
            cfg!.isPre ? '#d70a25' : 'var(--oio-button-primary-background)'
          };">
                </div>
                <textarea 
                  class="activity-textarea"
                  data-id="${cfg!.id}"
                  placeholder="活动规则" 
                  disabled
                  style="width: 100%;height: ${textareaHeight}px;border-radius: 4px;padding: 3px;resize: none;" 
                  rows="3">${generateActivityRuleStr(cfg.jsonData)}</textarea>
              </div>
            </div>
            ${generateActionBar(cfg)}
          </div>
        `
        }
      });
    }
  });
};
